import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { capture } from '../tracking/posthog';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import StepName           from './steps/StepName';
import StepStatus         from './steps/StepStatus';
import StepBeschaeftigung from './steps/StepBeschaeftigung';
import StepErwartung      from './steps/StepErwartung';
import StepStelle         from './steps/StepStelle';
import StepStammdaten     from './steps/StepStammdaten';
import StepReview         from './steps/StepReview';
import StepConversion     from './steps/StepConversion';
import StepAccount        from './steps/StepAccount';
import StepBumper         from './steps/StepBumper';
import StepSchneller      from './steps/StepSchneller';
import StepAbschluss      from './steps/StepAbschluss';
import { speichereOnboardingProfil, createProfil, createStelle } from '../api';
import { queueCvImport, savePreLoginFile, takePreLoginFile } from '../pendingCvImport';
import DotField from './DotField';
import { Postkarte, VorherNachher, JaStapel, Los } from './motifs/Motifs';
import './motifs/motifs.css';
import './OnboardingWizard.css';

const Karteikarte = () => (
  <div className="fkk-stage">
    <div className="fkk-group">
      <div className="fkk-back" />
      <div className="fkk-card">
        <div className="fkk-head">
          <span className="fkk-brand">CVFLOW · STECKBRIEF</span>
          <span className="fkk-no">Nr. 02</span>
        </div>
        <div className="fkk-title">Lass uns dich<br/>kennenlernen.</div>
        <div className="fkk-rule" />
        <div className="fkk-sec a">
          <div className="fkk-label">Status</div>
          <div className="fkk-rows">
            <div className="fkk-row"><span className="fkk-box"/><span className="fkk-bar" style={{width:'62%'}}/></div>
            <div className="fkk-row sel"><span className="fkk-box"/><span className="fkk-bar" style={{width:'74%'}}/></div>
            <div className="fkk-row"><span className="fkk-box"/><span className="fkk-bar" style={{width:'54%'}}/></div>
            <div className="fkk-row"><span className="fkk-box"/><span className="fkk-bar" style={{width:'68%'}}/></div>
          </div>
        </div>
        <div className="fkk-sec b">
          <div className="fkk-label">Fachbereich</div>
          <div className="fkk-chips">
            <span className="fkk-chip">IT / Software</span>
            <span className="fkk-chip">Marketing</span>
            <span className="fkk-chip sel">Ingenieurwesen</span>
            <span className="fkk-chip">Design</span>
            <span className="fkk-chip">Finanzen</span>
            <span className="fkk-chip">Vertrieb</span>
          </div>
        </div>
        <div className="fkk-foot">
          <span className="fkk-dot"/><span>Profil wird angelegt</span><span className="fkk-line"/>
        </div>
      </div>
      <div className="fkk-pin" />
    </div>
  </div>
);

const ONBOARDING_DRAFT_KEY = 'cvflow_onboarding_draft';

function ladeDraft() {
  try {
    const raw = localStorage.getItem(ONBOARDING_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const SCHRITTE = [
  {
    feld:       'name',
    eyebrow:    'Schritt 1 von 4',
    headline:   <>Willkommen!<span className="ob-headline-line2">Wie dürfen wir dich nennen?</span></>,
    subtext:    'Dein Name hilft uns, dein Profil persönlicher und klarer aufzubauen.',
    Komponente: StepName,
    rightContent: <Postkarte fill logoUrl="/logo.png" />,
  },
  {
    feld:       'status',
    eyebrow:    'Schritt 2 von 4',
    headline:   null,
    subtext:    null,
    Komponente: StepStatus,
    rightContent: <Karteikarte />,
  },
  {
    feld:       'beschaeftigung',
    eyebrow:    'Schritt 3 von 4',
    headline:   <>Was strebst <em>du an?</em></>,
    subtext:    'Diese Info hilft uns, die Anforderungen richtig einzuordnen.',
    Komponente: StepBeschaeftigung,
    rightImg:   '/onboarding/panel-2-status.png',
  },
  {
    feld:       null,
    isBumper:   true,
    eyebrow:    'Schritt 3 von 4',
    superlabel: 'So arbeitet CVFlow',
    headline:   <>Dein Lebenslauf wird ab heute <em>dynamisch.</em></>,
    subtext:    'Einmal einrichten – für immer passend.',
    Komponente: StepBumper,
    rightContent: <VorherNachher fill />,
  },
  {
    feld:       'erwartung',
    eyebrow:    'Schritt 4 von 4',
    headline:   <>Was erhoffst du dir von <em>CVFlow?</em></>,
    subtext:    'Wähle alles aus, was zutrifft.',
    Komponente: StepErwartung,
    rightContent: <JaStapel fill />,
  },
  {
    feld:       'stelle',
    eyebrow:    'Fast fertig',
    headline:   <>Hast du schon eine <em>Stelle im Blick?</em></>,
    subtext:    'Wenn ja, kann CVFlow später direkt mit deiner ersten Optimierung starten.',
    Komponente: StepStelle,
  },
  {
    feld:       'stammdaten',
    eyebrow:    null,
    headline:   <>Deine <em>Kontaktdaten</em></>,
    subtext:    'Werden direkt in dein Profil und deinen Lebenslauf eingetragen.',
    Komponente: StepStammdaten,
  },
  {
    feld:       'review',
    eyebrow:    null,
    headline:   <>Alles <em>auf einen Blick</em></>,
    subtext:    'Prüf noch einmal kurz deine Angaben.',
    Komponente: StepReview,
  },
  {
    feld:       'conversion',
    eyebrow:    'FAST FERTIG',
    headline:   <>Weniger Bewerbungsstress. Mehr Zeit für das <em>Wesentliche.</em></>,
    subtext:    null,
    Komponente: StepConversion,
  },
  {
    feld:       null,
    isBumper:   true,
    eyebrow:    'Fast geschafft!',
    superlabel: 'Dein Vorteil mit CVFlow',
    headline:   <>30<span className="ob-mal">×</span><em>schneller!</em></>,
    subtext:    'Eine Bewerbung in 2 Minuten statt einer Stunde.',
    Komponente: StepSchneller,
  },
  {
    feld:         null,
    isAccount:    true,
    hideWeiter:   true,
    eyebrow:      'Profil speichern',
    headline:     <>Speichere deinen <em>Fortschritt.</em></>,
    subtext:      'Deine Antworten bleiben nur lokal, bis du ein Konto erstellst oder dich einloggst.',
    Komponente:   StepAccount,
  },
  {
    feld:        null,
    isAbschluss: true,
    eyebrow:     'Fertig!',
    headline:    <>Alles bereit – <em>los geht's.</em></>,
    subtext:     'Dein Profil ist gespeichert. CVFlow optimiert ab jetzt jede Bewerbung für dich.',
    Komponente:  StepAbschluss,
    rightContent: <Los fill />,
  },
];

function istGueltig(feld, antworten) {
  if (!feld) return true;
  const wert = antworten[feld];
  if (feld === 'name') {
    return !!wert?.vorname?.trim() && !!wert?.nachname?.trim();
  }
  if (feld === 'status') {
    if (!wert?.status) return false;
    if (wert.status === 'arbeitslos' || wert.status === 'jobsuchend') return true;
    return !!wert.branche;
  }
  if (feld === 'erwartung') {
    return Array.isArray(wert) && wert.length > 0;
  }
  if (feld === 'beschaeftigung') return !!wert?.ziel;
  if (feld === 'stelle') {
    if (!wert?.modus) return false;
    if (wert.modus === 'spaeter') return true;
    return !!wert.inhalt?.trim();
  }
  if (feld === 'stammdaten') {
    return !!wert?.vorname?.trim() && !!wert?.nachname?.trim() && !!wert?.email?.trim() && !!wert?.cvDatei;
  }
  if (feld === 'review' || feld === 'conversion') return true;
  return wert !== undefined && wert !== null && wert !== '';
}

const HILFE_FRAGEN = [
  {
    frage: 'Wofür braucht CVFlow meine Daten?',
    antwort: 'Deine Angaben helfen CVFlow, jede Bewerbung automatisch auf die Stelle zuzuschneiden. Je genauer dein Profil, desto besser passen Anschreiben und Lebenslauf zur jeweiligen Ausschreibung.',
  },
  {
    frage: 'Werden meine Daten sicher gespeichert?',
    antwort: 'Ja. Alle Daten werden verschlüsselt auf europäischen Servern gespeichert und nicht an Dritte weitergegeben.',
  },
  {
    frage: 'Kann ich mein Profil später noch ändern?',
    antwort: 'Natürlich. Im Dashboard unter "Profil" kannst du alle Angaben jederzeit anpassen oder ergänzen.',
  },
  {
    frage: 'Was passiert, wenn ich das Onboarding überspringe?',
    antwort: 'CVFlow funktioniert auch ohne Onboarding, aber die Optimierungen sind dann weniger präzise. Du kannst das Profil jederzeit im Dashboard nachholen.',
  },
  {
    frage: 'Für welche Bewerbungsarten ist CVFlow geeignet?',
    antwort: 'CVFlow unterstützt Voll- und Teilzeitstellen, Praktika, Werkstudentenjobs und Ausbildungsplätze – in allen Branchen.',
  },
];

export default function OnboardingWizard() {
  const draft = useRef(ladeDraft());
  const draftSchritt = Math.min(draft.current?.schritt ?? 0, SCHRITTE.length - 1);
  const [schritt, setSchritt]     = useState(draftSchritt);

  useEffect(() => { capture('onboarding_viewed'); }, []);
  const [antworten, setAntworten] = useState(draft.current?.antworten ?? {});
  const [laden, setLaden]         = useState(false);
  const [abschlussFehler, setAbschlussFehler] = useState(null);
  const [helpOffen, setHelpOffen]           = useState(false);
  const [offeneFrageIdx, setOffeneFrageIdx] = useState(null);
  const helpRef = useRef(null);
  // ponytail: guards against StrictMode double-invoke (same fix as useFirstVisit)
  const abschlussGefeuertRef = useRef(false);

  useEffect(() => {
    if (!helpOffen) return;
    function handleClick(e) {
      if (helpRef.current && !helpRef.current.contains(e.target)) {
        setHelpOffen(false);
        setOffeneFrageIdx(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [helpOffen]);
  const navigate = useNavigate();

  const onboardingVorname = antworten.name?.vorname?.trim();
  const authVorname = auth.currentUser?.displayName?.split(' ')[0] || null;
  const vorname     = onboardingVorname || authVorname;
  const aktuell     = SCHRITTE[schritt];
  const gueltig     = istGueltig(aktuell.feld, antworten);
  const isBumper    = !!aktuell.isBumper;
  const isAbschluss = !!aktuell.isAbschluss;
  const isAccount   = !!aktuell.isAccount;

  const gesamtSchritte = SCHRITTE.length - 1; // Abschluss zählt nicht als nummerierter Schritt
  const fortschritt    = isAbschluss ? 100 : ((schritt + 1) / gesamtSchritte) * 100;
  const eyebrow        = isAbschluss ? 'Fertig!' : (aktuell.eyebrow || `Schritt ${schritt + 1} von ${gesamtSchritte}`);

  function setzeAntwort(feld, val) {
    setAntworten(a => ({ ...a, [feld]: val }));
  }

  function speichereDraft(nextSchritt = schritt, nextAntworten = antworten) {
    localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify({
      schritt: nextSchritt,
      antworten: nextAntworten,
      updatedAt: new Date().toISOString(),
    }));
  }

  useEffect(() => {
    speichereDraft(schritt, antworten);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schritt, antworten]);

  async function onboardingAbschliessen() {
    if (!auth.currentUser) {
      speichereDraft(schritt, antworten);
      // Datei vor dem Navigate retten — File-Objekte überleben keine localStorage-Serialisierung
      if (antworten.stammdaten?.cvDatei?.file instanceof File) {
        savePreLoginFile(antworten.stammdaten.cvDatei.file);
      }
      navigate('/login?mode=register');
      return;
    }

    setLaden(true);
    try {
      await speichereOnboardingProfil(antworten);

      // Auto-create profile from stammdaten collected in onboarding
      const stammdaten = antworten.stammdaten;
      if (stammdaten) {
        const payload = Object.fromEntries(
          Object.entries(stammdaten)
            .filter(([k]) => k !== 'cvDatei')
            .map(([k, v]) => [k, String(v || '').trim()])
        );
        let dateiname = null;
        try {
          const res = await createProfil(payload);
          dateiname = res.data.dateiname;
        } catch (err) {
          if (err?.response?.status === 409) {
            dateiname = `${payload.vorname}_${payload.nachname}`.toLowerCase().replace(/ /g, '_');
          }
        }
        if (dateiname) {
          const uid = auth.currentUser?.uid;
          const setupKey = uid
            ? `cvflow_first_profile_setup_done:${uid}`
            : 'cvflow_first_profile_setup_done';
          // Prefer live File object; fall back to pre-login saved file (email-registration path)
          const file = stammdaten.cvDatei?.file instanceof File
            ? stammdaten.cvDatei.file
            : takePreLoginFile();
          if (file) {
            // Queue the import — CvImportOverlay picks it up after navigation and shows loading UI
            queueCvImport(dateiname, file, setupKey);
          } else {
            localStorage.setItem(setupKey, '1');
          }
        }
      }

      // Auto-create stelle if user provided one
      const stelle = antworten.stelle;
      let neueStelle = null;
      if (stelle?.modus !== 'spaeter' && stelle?.inhalt?.trim()) {
        const stellePayload = { name: 'Neue Stellenanzeige' };
        if (stelle.modus === 'link') stellePayload.url = stelle.inhalt.trim();
        else stellePayload.text = stelle.inhalt.trim();
        try {
          const res = await createStelle(stellePayload);
          neueStelle = res.data;
        } catch {}
      }

      localStorage.setItem('onboarding_done', '1');
      localStorage.removeItem(ONBOARDING_DRAFT_KEY);
      localStorage.removeItem('cvflow_onboarding_profile_seed');
      navigate(neueStelle ? `/stelle/${neueStelle.id}` : '/dashboard');
    } finally {
      setLaden(false);
    }
  }

  useEffect(() => {
    if (isAccount && auth.currentUser) {
      if (abschlussGefeuertRef.current) return;
      abschlussGefeuertRef.current = true;
      onboardingAbschliessen().catch(() => { setAbschlussFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.'); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccount]);

  async function weiter() {
    if (isAbschluss) {
      await onboardingAbschliessen();
      return;
    }

    setSchritt(s => s + 1);
  }

  function zurueck() {
    if (schritt > 0) setSchritt(s => s - 1);
  }

  function ueberspringen() {
    localStorage.setItem('onboarding_done', '1');
    navigate('/login?mode=register');
  }

  function zuLogin(mode) {
    speichereDraft(schritt, antworten);
    if (antworten.stammdaten?.cvDatei?.file instanceof File) {
      savePreLoginFile(antworten.stammdaten.cvDatei.file);
    }
    navigate(`/login?mode=${mode}`);
  }

  const { Komponente, feld, superlabel, rightImg, rightContent } = aktuell;
  const hatRechts = !!(rightImg || rightContent);

  // Wenn ein Schritt ohne rechte Illustration auf einen mit Illustration folgt,
  // bleibt die alte Illustration kurz sichtbar, während die grüne Spalte sie überdeckt.
  const [ausblendendRechts, setAusblendendRechts] = useState(null);
  const letztesRechtsRef = useRef(null);
  const ausblendTimerRef = useRef(null);
  useLayoutEffect(() => {
    if (hatRechts) {
      letztesRechtsRef.current = { rightImg, rightContent };
      setAusblendendRechts(null);
      clearTimeout(ausblendTimerRef.current);
    } else if (letztesRechtsRef.current) {
      setAusblendendRechts(letztesRechtsRef.current);
      letztesRechtsRef.current = null;
      clearTimeout(ausblendTimerRef.current);
      ausblendTimerRef.current = setTimeout(() => setAusblendendRechts(null), 650);
    }
    return () => clearTimeout(ausblendTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schritt]);
  const anzeigeRechts = hatRechts ? { rightImg, rightContent } : ausblendendRechts;

  let { headline, subtext } = aktuell;
  if (aktuell.feld === 'status') {
    headline = vorname
      ? <>Willkommen, <em>{vorname}.</em><span className="ob-headline-line2">Lass uns dich kennenlernen.</span></>
      : <>Willkommen!<span className="ob-headline-line2">Lass uns dich kennenlernen.</span></>;
    subtext = null;
  }

  let weiterLabel = 'Weiter →';
  if (isBumper)    weiterLabel = 'Verstanden – Weiter →';
  else if (isAbschluss) weiterLabel = 'Jetzt loslegen →';

  return (
    <div className="ob-seite">
      <DotField
        dotRadius={1.5}
        dotSpacing={28}
        bulgeStrength={8}
        glowRadius={0}
        sparkle
        waveAmplitude={3}
        cursorRadius={80}
        cursorForce={0.05}
        bulgeOnly
        jitter={0.45}
        gradientFrom="rgba(249, 115, 22, 0.45)"
        gradientTo="rgba(255, 193, 0, 0.28)"
        glowColor="#F97316"
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}
      />
      <div className={`ob-wizard${helpOffen ? ' ob-wizard--help-offen' : ''}${!hatRechts ? ' ob-wizard--full' : ''}`}>

        {/* ── LEFT ── */}
        <div className="ob-left">
          <img className="ob-watermark" src="/logo.png" alt="" aria-hidden="true" />

          <div className="ob-left-body">
            {/* Progress */}
            <div className="ob-progress">
              <div className="ob-progress-track" />
              <div className="ob-progress-fill" style={{ width: `${fortschritt}%` }} />
              <span className="ob-progress-label">{eyebrow}</span>
            </div>

            {/* Logo */}
            {!isAccount && <img className="ob-logo" src="/logo.png" alt="CVFlow" />}

            {/* Superlabel */}
            {superlabel && <div className="ob-superlabel">{superlabel}</div>}

            {/* Headline + Subtext */}
            {headline && <h1 className="ob-headline">{headline}</h1>}
            {subtext && <p className="ob-subtext">{subtext}</p>}

            {/* Step content */}
            <Komponente
              key={schritt}
              wert={antworten[feld]}
              onChange={val => setzeAntwort(feld, val)}
              antworten={antworten}
              onWeiter={weiter}
              onGoogleFertig={onboardingAbschliessen}
              onEmailLogin={zuLogin}
              vorname={vorname}
            />

            <div className="ob-spacer" />

            {/* Back – bottom left */}
            {schritt > 0 && !isAbschluss && (
              <button className="ob-zurueck" onClick={zurueck} type="button">
                ← Zurück
              </button>
            )}

            {abschlussFehler && (
              <p style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                {abschlussFehler}
              </p>
            )}

            {!aktuell.hideWeiter && (
              <button
                className="ob-weiter-btn"
                onClick={() => { setAbschlussFehler(null); weiter(); }}
                disabled={!gueltig || laden}
                type="button"
              >
                {laden ? 'Speichert...' : weiterLabel}
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT ── */}
        {anzeigeRechts && (
          <div className="ob-right">
            {anzeigeRechts.rightImg
              ? <img src={anzeigeRechts.rightImg} alt="" className="ob-right-img" aria-hidden="true" />
              : anzeigeRechts.rightContent}
          </div>
        )}

        {!isAbschluss && (
          <button className="ob-skip-btn" type="button" onClick={ueberspringen}>
            Überspringen
          </button>
        )}
        <div ref={helpRef} className="ob-help-wrapper">
          {helpOffen && (
            <div className="ob-help-panel">
              <p className="ob-help-panel__title">Häufige Fragen</p>
              <ul className="ob-help-panel__list">
                {HILFE_FRAGEN.map((item, i) => {
                  const istOffen = offeneFrageIdx === i;
                  return (
                    <li key={i} className={`ob-help-panel__item${istOffen ? ' ob-help-panel__item--offen' : ''}`}>
                      <button
                        className="ob-help-panel__frage"
                        onClick={() => setOffeneFrageIdx(istOffen ? null : i)}
                        type="button"
                      >
                        <span>{item.frage}</span>
                        <span className="ob-help-panel__chevron">{istOffen ? '▲' : '▼'}</span>
                      </button>
                      {istOffen && (
                        <p className="ob-help-panel__antwort">{item.antwort}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <button
            className="ob-help-btn"
            type="button"
            onClick={() => setHelpOffen(o => !o)}
          >
            {helpOffen ? '✕ Schließen' : '? Hilfe'}
          </button>
        </div>

      </div>
    </div>
  );
}
